import { useEffect } from "react";

import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCurrentSectionId,
  selectCurrentSectionUserId,
  selectCurrentUserId,
} from "store/selectors";

/**
 * Ensures all user/course data that is required to resolve assessment data is fetched.
 * This does _not_ include assessment / submission / outcome-assessment data directly.
 */
export const useFetchAssessmentBaseData = () => {
  const { canParticipateInSection } = useCapabilities();
  const sectionId = useAppSelector(selectCurrentSectionId);
  const userId = useAppSelector(selectCurrentUserId);
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);

  // Fetching all assessment-relevant data for the current user
  useEffect(() => {
    if (!sectionUserId || !userId) {
      // This prevents an unecessary load of all course data for students while
      // the section users are still loading.
      return;
    }
    if (sectionId) {
      storeApi.outcomes.list({});
      storeApi.section_outcomes.list({ section_id: sectionId });
      storeApi.pages.list({
        section_id: sectionId,
        // Technically we only need the unreleased pages, but we already fetch all pages in other
        // views, so dropping this filter attribute allows us to reuse the cached data.
        // released_at__isnull: false,
      });
      // Only to resolve links to pages
      storeApi.topics.list({ section_id: sectionId });
      storeApi.activities.list({ section_id: sectionId });

      // This will only trigger for students, for now, as staff users will need access to _everyone's_
      // data, which is too much to load on the top level of the course.
      // If a userId is passed, it means an instructor is viewing a student's assessments, so we still
      // fetch the user data.
      if (canParticipateInSection) {
        storeApi.page_group_users.list({
          section_user_id: sectionUserId,
          section_id: sectionId,
        });
        storeApi.page_groups.list({
          pagegroupuser__section_user_id: sectionUserId,
          section_id: sectionId,
        });
      }
    }
  }, [sectionUserId, sectionId, userId, canParticipateInSection]);
};

// TODO: Look into optimizing this.  We shouldn't be doing fetching for multiple sections and
// we shouldn't be doing them one section at a time.
/**
 * Fetches assessment data for multiple sections.
 * This is the core logic that both single-section and multi-section functions use.
 * If a student accesses this, the backend will limit the query to only be for their own data.
 */
const useFetchAssessmentDataForSections = (sectionIds: string[]) => {
  const userId = useAppSelector(selectCurrentUserId);

  useEffect(() => {
    if (!userId || sectionIds.length === 0) {
      return;
    }

    // Fetch data for all sections
    // TODO: In the future, this could be optimized to use a single batch API call
    // to reduce network overhead and improve performance.
    const uniqueSectionIds = [...new Set(sectionIds)];

    uniqueSectionIds.forEach((sectionId) => {
      storeApi.section_outcomes.list({ section_id: sectionId });
      storeApi.page_outcomes.list({ section_id: sectionId });
      storeApi.pages.list({ section_id: sectionId });
      // We don't limit this to official submissions, as we might want to show comments from
      // unofficial submission assessments as well.
      storeApi.submissions.list({ section_id: sectionId });
      storeApi.assessments.list({ section_id: sectionId });
      storeApi.outcome_assessments.list({ section_id: sectionId });
    });
  }, [sectionIds, userId]);
};

/**
 * Ensures all assessments-related data for the current course is loaded.
 */
export const useFetchAssessmentData = () => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  useFetchAssessmentDataForSections(sectionId ? [sectionId] : []);
};
